import { datadogRum } from "@datadog/browser-rum";
import { MantineProvider } from "@mantine/core";
import { useAtomValue } from "jotai";
import { Helmet } from "react-helmet-async";

import AdditionalDocuments from "pages/AdditionalDocuments";
import ClaimSummary from "pages/ClaimSummary";
import DamageLocation from "pages/DamageLocation";
import DamagePhotos from "pages/DamagePhotos";
import DamageReview from "pages/DamageReview";
import DJLandingPage, {
  DJAccidentCircumstancePage,
  DJAccidentInformationPage,
  DJAccidentTimeAndLocationPage,
  DJAdditionalInformationPage,
  DJClaimSummaryPage,
  DJCompleteClaimInCustomerPortalPage,
  DJDamageLocationOtherVehiclePage,
  DJDamageLocationPHPage,
  DJDamagePhotosPage,
  DJFlowCompletedPage,
  DJHandlingDecisionPage,
  DJInjuredPeopleDetailsPage,
  DJOtherDriverDetailsPage,
  DJPersonalDetailsPHPage,
  DJPhotosAdditionalDocumentsPage,
  DJPhotosAuthoritiesPages,
  DJPhotosCAIPage,
  DJPhotosInjuredPeoplePage,
  DJSelectBodyShopPage,
  DJSelectWindowsDamaged,
  DJTypeOfClaimInsuranceToActivate,
  DJTypeOfClaimNaturalEvents,
  DJTypeOfClaimPage,
  DJTypeOfClaimTheftFire,
  DJVehicleConditionsPage,
  DJVehiclePhotosPage,
  DJVehicleTypeSelectionPage,
  DJWelcomePage,
} from "pages/DigitalJourneyPages";
import EstimateAnalysis from "pages/EstimateAnalysis";
import Estimate from "pages/EstimateRepariable";
import ExternalRedirect from "pages/ExternalRedirect";
import FlowCompleted from "pages/FlowCompleted";
import Onboarding from "pages/Onboarding";
import OutOfScope from "pages/OutOfScope";
import SpeedPayment, { NegotiateOffer, PayeeQuestions, PaymentAccepted, TalkToClaimHandler } from "pages/SpeedyPayment";
import { Payee } from "pages/SpeedyPayment/Payee/Payee";
import TotalLoss from "pages/TotalLoss";
import VehicleAnalysis from "pages/VehicleAnalysis";
import VehicleCondition from "pages/VehicleCondition";
import VehiclePhotos from "pages/VehiclePhotos";
import Vin from "pages/Vin";
import Welcome from "pages/Welcome";

import { BrowserNotSupported, ExpiredSession, GeneralError, InvalidSession, WrongDevice } from "components/ErrorStates";

import { clientConfigAtom } from "atoms/clientConfig";
import { payeeFinalisedAtom } from "atoms/payeeFinalised";
import { getUserLocale } from "utils/locale/userLocale";
import { Checkpoint } from "types/Checkpoint";

import { serverSessionAtom } from "../atoms/serverSession";
import { AppFlow, AppRoute, FlowPage, LandingPage } from "./AppFlow";
import { DigitalJourneyAppFlow } from "./DigitalJourneyAppFlow/DigitalJourneyAppFlow";
import { DigitalJourneyFlowPage } from "./DigitalJourneyAppFlow/DigitalJourneyFlowPage";
import { DigitalJourneyRoute } from "./DigitalJourneyAppFlow/constants";
import { useCacheBuster } from "./hooks/useCacheBuster";
import { useGetThemeOverride } from "./hooks/useGetThemeOverride";
import { useInitialiseConfigFromClientId } from "./hooks/useInitialiseConfigFromClientId";
import { useInitialiseEventTracking } from "./hooks/useInitialiseEventTracking";
import { useInitialiseLocalisation } from "./hooks/useInitialiseLocalisation";
import { useLastVisitedPage } from "./hooks/useLastVisitedPage";
import { useUnsupportedBrowsersRedirect } from "./hooks/useUnsupportedBrowsersRedirect";
import { useValidateSessionId } from "./hooks/useValidateSessionId";

export function App() {
  const serverSession = useAtomValue(serverSessionAtom);
  const hasVinInServerSession = !!serverSession?.vin;
  const hasPayeeEnabledInSession = !!serverSession?.enablePayeeJourney;
  const payeeFinalised = useAtomValue(payeeFinalisedAtom);
  const isDigitalJourneySession = !!serverSession?.digitalJourney;

  useCacheBuster();

  useInitialiseConfigFromClientId();

  useInitialiseLocalisation();

  useUnsupportedBrowsersRedirect();

  // Also stores session id if valid
  useValidateSessionId({
    onSuccess: (sessionId) => {
      datadogRum.addRumGlobalContext("session_id", sessionId);
    },
  });

  useInitialiseEventTracking();

  useLastVisitedPage();

  const {
    additionalDocuments,
    estimate,
    vin: vinConfig,
    favicon,
    theme: { font },
    damageReview,
    vehicleCondition,
    pointsOfImpact,
    welcome,
    flowCompletionRedirectUrl,
    enablePayeeJourney,
    speedyPayment,
    locale,
  } = useAtomValue(clientConfigAtom);

  const { themeOverride, stylesOverride } = useGetThemeOverride();

  let fontOrigin;
  try {
    fontOrigin = new URL(font.url).origin;
  } catch (err) {
    datadogRum.addError(err, {
      message: `Unable to parse font url, configuration value malformed`,
      url: font.url,
    });
  }

  return (
    <>
      <Helmet htmlAttributes={{ lang: getUserLocale(locale), translate: "no" }}>
        {fontOrigin && <link rel="preconnect" href={fontOrigin} />}
        <link href={font.url} rel="stylesheet" />
        {favicon && <link rel="icon" type="image/ico" href={favicon} />}
      </Helmet>
      <MantineProvider theme={themeOverride} styles={stylesOverride} withNormalizeCSS withGlobalStyles>
        {isDigitalJourneySession && !hasPayeeEnabledInSession ? (
          <>
            <Helmet>
              <script src="/stonly.js" />
            </Helmet>
          <DigitalJourneyAppFlow>
            <DigitalJourneyFlowPage path={DigitalJourneyRoute.Landing} element={<DJLandingPage />} />
            <DigitalJourneyFlowPage path={DigitalJourneyRoute.Welcome} element={<DJWelcomePage />} />
            <DigitalJourneyFlowPage path={DigitalJourneyRoute.VehicleType} element={<DJVehicleTypeSelectionPage />} />
            <DigitalJourneyFlowPage
              path={DigitalJourneyRoute.AccidentTimeAndLocation}
              element={<DJAccidentTimeAndLocationPage />}
            />
            <DigitalJourneyFlowPage path={DigitalJourneyRoute.TypeOfClaim} element={<DJTypeOfClaimPage />} />
            <DigitalJourneyFlowPage
              path={DigitalJourneyRoute.SelectWindowsDamaged}
              element={<DJSelectWindowsDamaged />}
            />
            <DigitalJourneyFlowPage
              path={DigitalJourneyRoute.TypeOfClaimTheftFire}
              element={<DJTypeOfClaimTheftFire />}
            />
            <DigitalJourneyFlowPage
              path={DigitalJourneyRoute.TypeOfClaimNaturalEvent}
              element={<DJTypeOfClaimNaturalEvents />}
            />
            <DigitalJourneyFlowPage
              path={DigitalJourneyRoute.TypeOfClaimInsuranceToActivate}
              element={<DJTypeOfClaimInsuranceToActivate />}
            />
            <DigitalJourneyFlowPage
              path={DigitalJourneyRoute.AccidentInformation}
              element={<DJAccidentInformationPage />}
            />
            <DigitalJourneyFlowPage
              path={DigitalJourneyRoute.CompleteClaimInCustomerPortal}
              element={<DJCompleteClaimInCustomerPortalPage />}
            />
            <DigitalJourneyFlowPage
              path={DigitalJourneyRoute.AccidentCircumstance}
              element={<DJAccidentCircumstancePage />}
            />
            <DigitalJourneyFlowPage path={DigitalJourneyRoute.VehicleCondition} element={<DJVehicleConditionsPage />} />
            <DigitalJourneyFlowPage path={DigitalJourneyRoute.DamageLocationPH} element={<DJDamageLocationPHPage />} />
            <DigitalJourneyFlowPage
              path={DigitalJourneyRoute.PHPersonalDetails}
              element={<DJPersonalDetailsPHPage />}
            />
            <DigitalJourneyFlowPage
              path={DigitalJourneyRoute.OtherDriverPersonalDetails}
              element={<DJOtherDriverDetailsPage />}
            />
            <DigitalJourneyFlowPage
              path={DigitalJourneyRoute.DamageLocationOther}
              element={<DJDamageLocationOtherVehiclePage />}
            />
            <DigitalJourneyFlowPage
              path={DigitalJourneyRoute.InjuredPeopleDetails}
              element={<DJInjuredPeopleDetailsPage />}
            />
            <DigitalJourneyFlowPage path={DigitalJourneyRoute.CAIPhotos} element={<DJPhotosCAIPage />} />
            <DigitalJourneyFlowPage
              path={DigitalJourneyRoute.InjuredPeopleDocumentPhotos}
              element={<DJPhotosInjuredPeoplePage />}
            />
            <DigitalJourneyFlowPage
              path={DigitalJourneyRoute.AuthoritiesDocumentsPhotos}
              element={<DJPhotosAuthoritiesPages />}
            />
            <DigitalJourneyFlowPage
              path={DigitalJourneyRoute.AdditionalDocumentsPhotos}
              element={<DJPhotosAdditionalDocumentsPage />}
            />
            <DigitalJourneyFlowPage
              path={DigitalJourneyRoute.AdditionalInformation}
              element={<DJAdditionalInformationPage />}
            />
            <DigitalJourneyFlowPage path={DigitalJourneyRoute.HandlingDecision} element={<DJHandlingDecisionPage />} />
            <DigitalJourneyFlowPage path={DigitalJourneyRoute.SelectBodyShop} element={<DJSelectBodyShopPage />} />
            <DigitalJourneyFlowPage path={DigitalJourneyRoute.DamagePhotoCapture} element={<DJDamagePhotosPage />} />
            <DigitalJourneyFlowPage path={DigitalJourneyRoute.VehiclePhotoCapture} element={<DJVehiclePhotosPage />} />
            <DigitalJourneyFlowPage path={DigitalJourneyRoute.ClaimSummary} element={<DJClaimSummaryPage />} />
            <DigitalJourneyFlowPage path={DigitalJourneyRoute.FlowCompleted} element={<DJFlowCompletedPage />} />

            {/* Other routes not part of the app flow */}
            <DigitalJourneyFlowPage path={DigitalJourneyRoute.WrongDevice} element={<WrongDevice />} />
            <DigitalJourneyFlowPage path={DigitalJourneyRoute.InvalidSession} element={<InvalidSession />} />
            <DigitalJourneyFlowPage path={DigitalJourneyRoute.ExpiredSession} element={<ExpiredSession />} />
            <DigitalJourneyFlowPage path={DigitalJourneyRoute.BrowserNotSupported} element={<BrowserNotSupported />} />
            <DigitalJourneyFlowPage path={DigitalJourneyRoute.ApiError} element={<GeneralError />} />
            <DigitalJourneyFlowPage path={DigitalJourneyRoute.CatchAll} element={<GeneralError />} />
          </DigitalJourneyAppFlow>
          </>
        ) : (
          <AppFlow>
            <FlowPage path={AppRoute.Landing} element={<LandingPage />} />
            {enablePayeeJourney && hasPayeeEnabledInSession && (
              <>
                {speedyPayment?.showRepairablePage && (
                  <FlowPage path={AppRoute.EstimateRepariablePayee} element={<Estimate />} />
                )}
                {!payeeFinalised && (
                  <>
                    <FlowPage path={AppRoute.SpeedyPayment} element={<SpeedPayment />} />
                    {speedyPayment?.questions && (
                      <FlowPage path={AppRoute.PayeeAcceptance} element={<PayeeQuestions />} />
                    )}
                    <FlowPage path={AppRoute.PayeeDetails} element={<Payee />} />
                  </>
                )}
                <FlowPage
                  path={AppRoute.PaymentAccepted}
                  element={<PaymentAccepted />}
                  checkpoint={Checkpoint.FLOW_COMPLETED}
                />
                <FlowPage
                  path={AppRoute.TalkToClaimHandler}
                  element={<TalkToClaimHandler />}
                  checkpoint={Checkpoint.FLOW_COMPLETED}
                />
              </>
            )}
            {!welcome.disabled && <FlowPage path={AppRoute.Welcome} element={<Welcome />} />}
            <FlowPage path={AppRoute.Onboarding} element={<Onboarding />} />
            {!vehicleCondition.disabled && (
              <FlowPage path={AppRoute.VehicleCondition} element={<VehicleCondition />} showInProgressStepper />
            )}
            {!pointsOfImpact?.disabled && (
              <FlowPage path={AppRoute.DamageLocation} element={<DamageLocation />} showInProgressStepper />
            )}
            <FlowPage path={AppRoute.DamagePhotos} element={<DamagePhotos />} showInProgressStepper />
            <FlowPage path={AppRoute.VehiclePhotos} element={<VehiclePhotos />} showInProgressStepper />
            {additionalDocuments && (
              <FlowPage path={AppRoute.AdditionalDocuments} element={<AdditionalDocuments />} showInProgressStepper />
            )}
            {vinConfig && !hasVinInServerSession && (
              <FlowPage path={AppRoute.Vin} element={<Vin />} showInProgressStepper />
            )}
            <FlowPage path={AppRoute.ClaimSummary} element={<ClaimSummary />} showInProgressStepper />
            <FlowPage
              path={AppRoute.VehicleAnalysis}
              element={<VehicleAnalysis />}
              checkpoint={Checkpoint.CLAIM_SUBMITTED}
            />
            {!damageReview?.disabled && (
              <FlowPage path={AppRoute.DamageReview} element={<DamageReview />} showInProgressStepper />
            )}
            {estimate && (
              <>
                <FlowPage
                  path={AppRoute.EstimateAnalysis}
                  element={<EstimateAnalysis />}
                  checkpoint={Checkpoint.DAMAGED_PARTS_SUBMITTED}
                />
                <FlowPage path={AppRoute.EstimateRepariable} element={<Estimate />} showInProgressStepper />
                {estimate.repairable?.withSpeedyPayment && (
                  <>
                    <FlowPage path={AppRoute.SpeedyPayment} element={<SpeedPayment />} showInProgressStepper />
                    <FlowPage
                      path={AppRoute.PaymentAccepted}
                      element={<PaymentAccepted />}
                      checkpoint={Checkpoint.FLOW_COMPLETED}
                    />
                    <FlowPage
                      path={AppRoute.TalkToClaimHandler}
                      element={<TalkToClaimHandler />}
                      checkpoint={Checkpoint.FLOW_COMPLETED}
                    />

                    {estimate.repairable.withNegotiation && (
                      <FlowPage path={AppRoute.NegotiateOffer} element={<NegotiateOffer />} />
                    )}
                  </>
                )}
                <FlowPage
                  path={AppRoute.EstimateTotalLoss}
                  element={<TotalLoss />}
                  checkpoint={Checkpoint.FLOW_COMPLETED}
                />
                <FlowPage path={AppRoute.EstimateOutOfScope} element={<OutOfScope />} />
              </>
            )}
            {!!flowCompletionRedirectUrl && (
              <FlowPage path={AppRoute.ExternalRedirect} element={<ExternalRedirect />} />
            )}
            <FlowPage
              path={AppRoute.FlowCompleted}
              element={<FlowCompleted />}
              {...(!enablePayeeJourney ? { checkpoint: Checkpoint.FLOW_COMPLETED } : {})}
            />

            {/* Other routes not part of the app flow */}
            <FlowPage ignoreFromAppFlow path={AppRoute.WrongDevice} element={<WrongDevice />} />
            <FlowPage ignoreFromAppFlow path={AppRoute.InvalidSession} element={<InvalidSession />} />
            <FlowPage ignoreFromAppFlow path={AppRoute.ExpiredSession} element={<ExpiredSession />} />
            <FlowPage ignoreFromAppFlow path={AppRoute.BrowserNotSupported} element={<BrowserNotSupported />} />
            <FlowPage ignoreFromAppFlow path={AppRoute.ApiError} element={<GeneralError />} />
            <FlowPage ignoreFromAppFlow path={AppRoute.CatchAll} element={<GeneralError />} />
          </AppFlow>
        )}
      </MantineProvider>
    </>
  );
}
